import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import { IconButton } from "@mui/material";
import moment from "moment";

import styled from "styled-components";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import BlockTitle from "../Common/BlockTitle";

import { ScheduleItemComponent, ScheduleItemSkeleton } from "./ScheduleItem";
import { useScheduleContext } from "../../contexts/schedule";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";

const SliderContainer = styled.div`
  gap: 16px;
  max-width: 100%;
  margin: 0 auto;
`;

const StyledSwiper = styled(Swiper)`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  transition: all 0.3s linear;
`;

const StyledIconPrev = styled.i`
  &.swiper-button-disabled {
    background-color: transparent;
    opacity: 0.3;
    pointer-events: none;
    cursor: default;
  }
`;

const StyledIconNext = styled.i`
  &.swiper-button-disabled {
    background-color: transparent;
    opacity: 0.3;
    pointer-events: none;
    cursor: default;
  }
`;

const preSeasonRaces = [
];

// Pre-season races are kept by hand because they are not league rounds in the database.
const mockRaces = preSeasonRaces.map((race, index) => ({
  ...race,
  key: `pre-season-${index}`,
  subtitle: race.subtitle || "Pre-Season Race",
}));

export default function Slider() {
  const { seasons, currentSeasonId, loadSchedule } = useScheduleContext();

  // The calendar follows the current season, which is the one place `currentSeasonId` is the right
  // answer: a season that has just been created should publish its schedule here before it has
  // raced. Standings deliberately follow a different season until this one scores.
  const scheduleSeason = currentSeasonId ? seasons[currentSeasonId] : null;

  // A parent season holds no races of its own -- its children do -- so the calendar is the union of
  // the children's schedules. Keyed as a string because `seasons` gets a new identity on every
  // merge, and an array dep would re-run this effect forever.
  const childSeasonIdsKey = (scheduleSeason?.childSeasons || []).map((child) => child.id).join(",");

  useEffect(() => {
    if (currentSeasonId) {
      loadSchedule(currentSeasonId);
    }
    // eslint-disable-next-line
  }, [currentSeasonId]);

  useEffect(() => {
    if (childSeasonIdsKey) {
      loadSchedule(childSeasonIdsKey.split(",").map(Number));
    }
    // eslint-disable-next-line
  }, [childSeasonIdsKey]);

  // null while the schedule is still arriving, [] once we know there is nothing to show
  const races = useMemo(() => {
    if (!scheduleSeason) return mockRaces.length > 0 ? mockRaces : null;

    const childIds = childSeasonIdsKey ? childSeasonIdsKey.split(",").map(Number) : [];
    const sources =
      childIds.length > 0 ? childIds.map((id) => seasons[id]).filter(Boolean) : [scheduleSeason];

    if (sources.length === 0) return mockRaces.length > 0 ? mockRaces : null;

    const seen = new Set();
    const collected = sources.flatMap((season) => {
      const ownRaces = season.races || [];
      return ownRaces.map((race, index) => ({
        ...race,
        key: `${race.seasonId ?? season.id}-${race.id}`,
        seasonId: race.seasonId ?? season.id,
        // Round number within the season that actually owns the race, so each child of a parent
        // season counts from one.
        subtitle: race.pointsRace ? `${season.shortName}, Етап ${index + 1}` : "Pre-Season Race",
      }));
    });

    // Sorted together so a pre-season race sits in date order among the season rounds.
    return [...mockRaces, ...collected]
      .filter((race) => {
        if (seen.has(race.key)) return false;
        seen.add(race.key);
        return true;
      })
      .sort((a, b) => moment(a.date, "YYYY-MM-DD") - moment(b.date, "YYYY-MM-DD"));
  }, [scheduleSeason, childSeasonIdsKey, seasons]);

  const [swiper, setSwiper] = useState(null);
  const [closestRaceIndex, setClosestRaceIndex] = useState(null);

  const slideTo = useCallback(
    (index) => {
      if (swiper) {
        swiper.slideTo(index, 1200);
      }
    },
    [swiper],
  );

  // Open on the most recent race that has already happened, so the round in progress is in view.
  // A season that has not started yet stays on round one.
  useEffect(() => {
    if (!races || races.length === 0) return;

    const today = moment().startOf("day");
    let lastPast = 0;
    races.forEach((race, index) => {
      if (moment(race.date, "YYYY-MM-DD") < today) {
        lastPast = index;
      }
    });
    setClosestRaceIndex(lastPast);
  }, [races]);

  useEffect(() => {
    // `closestRaceIndex` of 0 is a real position, not "unset".
    if (!swiper || closestRaceIndex === null) return;

    const timeout = setTimeout(() => slideTo(closestRaceIndex), 500);
    return () => clearTimeout(timeout);
  }, [swiper, closestRaceIndex, slideTo]);

  if (races && races.length === 0) return null;

  return (
    <>
      <BlockTitle style={{ marginTop: 36 }}>
        Календар
        <div>
          <StyledIconPrev className="icon-arrow-long-left review-swiper-button-prev-schedule-slider">
            <IconButton>
              <KeyboardArrowLeftIcon sx={{ fontSize: "38px" }} />
            </IconButton>
          </StyledIconPrev>

          <StyledIconNext className="icon-arrow-long-right review-swiper-button-next-schedule-slider">
            <IconButton>
              <KeyboardArrowRightIcon sx={{ fontSize: "38px" }} />
            </IconButton>
          </StyledIconNext>
        </div>
      </BlockTitle>
      <SliderContainer>
        <StyledSwiper
          onSwiper={setSwiper}
          style={{ width: "100%" }}
          speed={1000}
          initialSlide={0}
          watchSlidesProgress={false}
          slidesPerView={1}
          slidesPerGroup={1}
          spaceBetween={16}
          navigation={{
            nextEl: ".review-swiper-button-next-schedule-slider",
            prevEl: ".review-swiper-button-prev-schedule-slider",
          }}
          modules={[Navigation, Pagination]}
          breakpoints={{
            480: {
              slidesPerView: 2,
            },
            860: {
              slidesPerView: 3,
            },
            1200: {
              slidesPerView: 4,
            },
          }}
        >
          {races
            ? races.map((item) => (
                <SwiperSlide key={item.key}>
                  <ScheduleItemComponent {...item} />
                </SwiperSlide>
              ))
            : [...new Array(4)].map((value, index) => (
                <SwiperSlide key={index}>
                  <ScheduleItemSkeleton />
                </SwiperSlide>
              ))}
        </StyledSwiper>
      </SliderContainer>
    </>
  );
}
